import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next'
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import * as Yup from 'yup';
import { useFormik } from 'formik'
import { v4 as uuidv4 } from 'uuid';
import { inventoryService, locationService, productService, supplierService } from 'services'
import { capitalize, capitalizeFirstLetter, closeModal, doAmountFormat, doDateFormatWithTime, getPaymentModeLabel, routes, stock_status } from 'utils'
import { _Object } from 'utils/interfaces'
import { Breadcrumbs, InputField, SEOHeader } from 'views/components'
import { AsyncSelectField, Button, SearchField, SelectField, TextArea } from 'views/components/form-inputs'
import { purchaseService } from 'services/purchase.serveice';
import { mediaService } from 'services/media.serveice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import moment from 'moment';

const dateOnly = Yup.string()
  .matches(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format')
  .nullable();

type PreviewFile = {
  file: File;
  url: string;
  type: 'image' | 'pdf' | 'other';
};

const getFileType = (file: any) => {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type === 'application/pdf') return 'pdf';
  return 'other';
};

const PurchaseReportCreate = () => {
  const selectAsyncSelect: any = useRef([])
  const { t } = useTranslation()
  const navigate = useNavigate();
  const { id } = useParams()

  const inputRef = useRef<HTMLInputElement | null>(null);

  const { me } = useSelector((state: RootState) => state.session)

  const [loading, setLoading] = useState<_Object>({
    search: false,
    btn: false,
    item: false,
    image: false,
    main: false
  })
  const [products, setProducts] = useState<_Object | null>(null)
  const [modalState, setModalState] = useState(false);
  const [suppliers, setSuppliers] = useState([])
  const [files, setFiles] = useState<PreviewFile[]>([]);
  const [initialValues, setInitialValues] = useState<_Object>({
    supplier_id: '',
    amount: '',
    invoice_number: '',
    paid_amount: 0,
    payment_ref_number: '',
    invoice_date: null,
    receiving_date: null,
    payment_mode: null,
    note: ''
  })
  const [items, setItems] = useState<_Object>([])
  const [locations, setLocations] = useState<_Object>([])
  const [payments, setPayments] = useState<_Object>([])

  const formik: any = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: Yup.object({
      supplier_id: Yup.string().label('Supplier').required(),
      amount: Yup.number()
        .typeError('Amount must be a number')
        .label('Amount')
        .required()
        .min(0, 'Amount must be greater than 0'),
      invoice_number: Yup.string().label('Invoice number').required(),
      paid_amount: Yup.number()
        .transform((value, originalValue) =>
          originalValue === '' || originalValue === null ? undefined : value
        )
        .nullable()
        .min(0, 'Paid amount must be 0 or greater')
        .max(Yup.ref('amount'), 'Paid amount must be less than or equal to amount')
        .optional(),
      payment_mode: Yup.string().when('paid_amount', {
        is: (val: any) => Number(val) > 0,
        then: (schema) => schema.required('Payment mode is required'),
        otherwise: (schema) => schema.notRequired()
      }),
      payment_ref_number: Yup.string().when('paid_amount', {
        is: (val: any) => Number(val) > 0,
        then: (schema) => schema.required('Payment reference number is required'),
        otherwise: (schema) => schema.notRequired()
      }),
      invoice_date: dateOnly.optional(),
      receiving_date: dateOnly.optional(),
      note: Yup.string().optional(),
    }),
    onSubmit: async (values: _Object) => {
      try {
        setLoading({ btn: true });
        const res = await purchaseService.createPurchase(values)
        if (res.error === false) {
          if (files.length > 0) {
            const formData = new FormData()

            formData.append('entityType', 'purchase')
            formData.append('key', 'purchaseMedia')
            formData.append('entityId', res?.data?.id || '')

            files.forEach((file: PreviewFile) => {
              formData.append('files', file.file, file.file.name)
            })

            const media = await mediaService.uploadMedia(formData)

            if (media.error === false) {
              setLoading({ btn: false })
              navigate(`${routes.purchaseReport}/${res?.data?.id}`)
            } else {
              setLoading({ btn: false })
            }
          } else {
            setLoading({ btn: false })
            navigate(`${routes.purchaseReport}/${res?.data?.id}`)
          }
        } else {
          setLoading({ btn: false })
        }
      } catch (error) {
        setLoading({ btn: false })
        console.log('error :>> ', error);
      }
    },
  });

  const searchFormik = useFormik({
    initialValues: { q: '' },

    onSubmit: () => {
      if (searchFormik.values.q.trim() !== '') {
        setLoading({ search: true });
        productService.getProducts({ q: searchFormik.values.q.trim() }).then((data: _Object) => {
          setProducts(data?.items)
          if (data?.items?.length > 1) {
            setModalState(true);
          } else {
            formik.setFieldValue('product_id', data?.items[0]?.id);
            formik.setFieldValue('product', data?.items[0]);
          }
          setLoading({ search: false })
        })
      }
    },
  });

  const addItemFormik: any = useFormik({
    initialValues: {
      batchid: uuidv4(),
      product_id: '',
      quantity: '',
      location_id: '',
      purchase_id: id,
    },
    validationSchema: Yup.object({
      location_id: Yup.string().label('Location').required(),
      quantity: Yup.number().label('Quantity').required()
        .min(0, 'Quantity must be greater than 0')
    }),
    onSubmit: (values: any, { resetForm }) => {
      setLoading({ item: true });
      const payload = {
        batchid: uuidv4(),
        product_id: values.product_id,
        quantity: values.quantity,
        location_id: values.location_id,
        purchase_id: id,
        status: 'received',
        order_id: null
      }
      inventoryService.createInventory(payload).then((data: _Object) => {
        setLoading({ item: false });
        if (data.error === false) {

          const newData = {
            product: values.product,
            quantity: values.quantity,
          };

          setItems((prev: _Object[]) => [...prev, newData]);
          resetForm();
          setProducts(null)
          searchFormik.setFieldValue('q', '')
        }
      }).catch((error) => {
        console.log('error :>> ', error);
        setLoading({ item: false });
      })
    },
  });

  useEffect(() => {
    supplierService.getSuppliers({}).then((data) => {
      setSuppliers(data.items)
    })

    locationService.getLocations('').then((data: _Object) => {
      setLocations(data.items)
    })
  }, [])

  const orderOption = async (keywords: string) => {
    const data: _Object = await supplierService.getSuppliers({ q: keywords })
    return data.items?.map((item: _Object) => { return { label: capitalize(item.name), value: item.id } })
  }

  const locationOption = async (locationValue: string) => {
    const data: _Object = await locationService.getLocations({ q: locationValue })
    return data.items?.map((item: _Object) => { return { label: capitalize(item.name), value: item.id } })
  }

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoading({ image: true })
    const selected = e.target.files ? Array.from(e.target.files) : [];
    if (!selected.length) return;

    const mapped: PreviewFile[] = selected.map((file) => ({
      file,
      url: URL.createObjectURL(file),
      type: getFileType(file),
    }));

    if (id) {
      const formData = new FormData()

      formData.append('entityType', 'purchase')
      formData.append('key', 'purchaseMedia')
      formData.append('entityId', id || '')

      selected.forEach((file) => {
        formData.append('files', file, file.name)
      })

      const media = await mediaService.uploadMedia(formData)

      if (media.error === false) {
        setLoading({ image: false })
        setFiles((prev) => [...prev, ...mapped]);
      } else {
        setLoading({ image: false })
      }
    } else {
      setFiles((prev) => [...prev, ...mapped]);
      setLoading({ image: false })
    }
    e.target.value = '';
  };

  const removeFile = async (index: number, id?: string) => {
    if (id) {
      const res = await mediaService.deleteMedia(id)
      if (res.error === false) {
        setFiles((prev) => {
          const copy = [...prev];
          const item = copy[index];
          if (item?.url) URL.revokeObjectURL(item.url);
          copy.splice(index, 1);
          return copy;
        });
      }
    } else {
      setFiles((prev) => {
        const copy = [...prev];
        const item = copy[index];
        if (item?.url) URL.revokeObjectURL(item.url);
        copy.splice(index, 1);
        return copy;
      });
    }
  };

  useEffect(() => {
    if (!id) return;

    const fetchDetails = async () => {
      setLoading({ main: true })
      try {
        const res = await purchaseService.getPurchaseDetails(id);

        if (res) {
          setInitialValues(res)
          if (res.media?.length) {
            const mapped: any = res.media.map((file: any) => ({
              url: `${process.env.REACT_APP_BASE_URL}/media/${file.path}`,
              type: getFileType({ type: file.mime_data }),
              file: { name: file.original_name },
              id: file?.id
            }));
            setFiles(mapped)
          }
          if (res.payments?.length > 0) {
            setPayments(res.payments)
          }
          if (res.items?.length > 0) {
            setItems(res.items)
          }
          setLoading({ main: false })
        }
      } catch (err) {
        setLoading({ main: false })
        console.log('error :>> ', err);
      }
    };

    fetchDetails();
  }, [id]);

  const addPaymentFormik: any = useFormik({
    initialValues: {
      amount: '',
      payment_ref_number: '',
      payment_mode: '',
      payment_date: '',
      status: 'paid',
      purchase_id: id,
    },
    validationSchema: Yup.object({
      amount: Yup.number()
        .typeError('Amount must be a number')
        .label('Amount')
        .required()
        .min(0, 'Amount must be greater than 0'),
      payment_ref_number: Yup.string().label('Reference no.').required(),
      payment_mode: Yup.string().label('Payment mode').required(),
      payment_date: Yup.string().label('Payment date').required(),
    }),
    onSubmit: async (values: any, { resetForm }) => {
      setLoading({ payment: true });
      try {
        const res = await purchaseService.addPurchasePayment(values)

        if (res.error === false) {
          setPayments((prev: _Object[]) => [...prev, ...[values]]);
          setLoading({ payment: false });
          closeModal('addPaymentModal');
          resetForm()
        } else {
          setLoading({ payment: false });
        }
      } catch (error) {
        console.log('error :>> ', error);
        setLoading({ payment: false });
      }
    },
  });

  return (
    <>
      <SEOHeader title="Create purchase" />

      <Breadcrumbs
        trails={[
          {
            label: t('purchase.purchases'),
            path: `${routes.purchaseReport}`
          },
          {
            label: id ? `${t('purchase.detail')}` : `${t('purchase.add')}`,
            path: ''
          }
        ]}
        pageTitle={id ? `${t('purchase.detail')}` : `${t('purchase.add')}`}
      />


      <div className={`row new-inventory ${loading.main === true ? 'is-loading' : ''}`}>
        <div className="col-12 col-md-6 col-lg-6 col-xl-6 col-xxl-6">
          <div className="card">
            <div className="card-header p-3">
              <h4 className="mb-0">{id ? `${t('purchase.detail')}` : `${t('purchase.add')}`}</h4>
            </div>

            <div className="card-body">
              <form onSubmit={formik.handleSubmit}>
                <div className="row">
                  <div className="col-4">
                    <AsyncSelectField
                      isClearable
                      cacheOptions
                      required={true}
                      label={`${t('supplier.supplier')}`}
                      name="supplier_id"
                      placeholder={t('common.choose_option')}
                      loadOptions={orderOption}
                      ref={(ref: any) => (selectAsyncSelect.current = ref)}
                      defaultOptions={suppliers?.map((item: _Object) => { return { label: item.name, value: item.id } })}
                      onChange={(val: _Object) => {
                        formik.setFieldValue('supplier_id', val?.value || '')
                      }}
                      value={
                        suppliers
                          ?.map((item: _Object) => ({ label: item.name, value: item.id }))
                          .find((opt: any) => opt.value === formik.values.supplier_id) || null
                      }
                      onBlur={formik.handleBlur}
                      error={formik.touched.supplier_id && formik.errors?.supplier_id}
                    />
                  </div>

                  <InputField
                    className="col-4"
                    type="text"
                    name="invoice_number"
                    label={t('common.invoice_number')}
                    required={true}
                    value={formik.values.invoice_number}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.invoice_number && formik.errors.invoice_number}
                  />

                  <InputField
                    className="col-4"
                    type="number"
                    name="amount"
                    label={t('common.amount')}
                    required={true}
                    value={formik.values.amount}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.amount && formik.errors.amount}
                  />
                </div>


                <div className="row">
                  <div className="col-6 form-group mb-3">
                    <label>{t('common.invoice_date')}</label>
                    <DatePicker
                      selected={formik.values.invoice_date ? new Date(formik.values.invoice_date) : null}
                      onChange={(date: Date | null) => {
                        formik.setFieldValue('invoice_date', date ? date.toISOString().slice(0, 10) : '')
                      }}
                      dateFormat="MMM dd, yyyy"
                      placeholderText="Select end date"
                      className="form-control"
                    />
                  </div>

                  <div className="col-6 form-group mb-3">
                    <label>{t('common.receiving_date')}</label>
                    <DatePicker
                      selected={formik.values.receiving_date ? new Date(formik.values.receiving_date) : null}
                      onChange={(date: Date | null) => {
                        formik.setFieldValue('receiving_date', date ? date.toISOString().slice(0, 10) : '')
                      }}
                      dateFormat="MMM dd, yyyy"
                      placeholderText="Select end date"
                      className="form-control"
                    />
                  </div>

                  {!id &&
                    <>
                      <InputField
                        className="col-4"
                        type="number"
                        name="paid_amount"
                        label={t('common.paid_amount')}
                        value={formik.values.paid_amount === 0 ? '' : formik.values.paid_amount}
                        onChange={formik.handleChange}
                        error={formik.touched.paid_amount && formik.errors.paid_amount}
                      />

                      <SelectField
                        name="payment_mode"
                        label={t('common.payment_mode')}
                        className="col-4"
                        options={[
                          { value: 'cash', label: 'Cash' },
                          { value: 'upi', label: 'UPI' },
                          { value: 'card', label: 'Debit/Credit Card' },
                        ]}
                        value={{ value: formik.values.payment_mode }}
                        onChange={(e: any) => {
                          formik.setFieldValue('payment_mode', e.value)
                        }}
                        error={formik.touched.payment_mode && formik.errors.payment_mode}
                      />


                      <InputField
                        className="col-4"
                        type="text"
                        name="payment_ref_number"
                        label={`${t('common.payment_ref')} #`}
                        value={formik.values.payment_ref_number}
                        onChange={formik.handleChange}
                        error={formik.touched.payment_ref_number && formik.errors.payment_ref_number}
                      />
                    </>
                  }

                  <TextArea
                    className="col-12"
                    name="note"
                    label={t('common.note')}
                    placeholder={t('checkout.enter_note')}
                    value={formik.values.note}
                    onChange={formik.handleChange}
                  />

                  {/* <div className="row">
                    <div>

                      <ul className="list-inline d-flex align-items-center">

                        <li className="list-inline-item upload-box img-upload-list"><img src="https://res.cloudinary.com/dzsl8v8yw/image/fetch/e_vibrance:100/c_limit,w_1920/f_auto/q_auto/v20468/https://d31sl6cu4pqx6g.cloudfront.net/Tour-Images/Final/IMG-Worlds-of-Adventure-4753/1760008208926_3_2.jpg?_a=BAVAZGE70" className="img-fluid" alt="" /></li>

                        <li className="list-inline-item">

                          <label
                            htmlFor="imageUpload"
                            className="upload-box d-flex flex-column align-items-center justify-content-center p-4"
                          >
                            <h6 className="mt-2 mb-1">Upload file</h6>
                            <small className="allow-content">PNG, JPG, PDF allow</small>

                            <input
                              type="file"
                              id="imageUpload"
                              accept="image/*"
                              className="d-none"
                            />
                          </label>
                        </li>

                      </ul>
                    </div>
                  </div> */}

                  <div className="row">
                    <div>
                      <ul className="list-inline d-flex align-items-center flex-wrap gap-2 mb-0">
                        {files.map((item: any, index) => (
                          <li
                            key={index}
                            className="list-inline-item img-upload-list position-relative"
                            style={{ width: 160, height: 110 }}
                          >
                            {item.type === 'image' && (
                              <img
                                src={item.url}
                                className="img-fluid"
                                alt={item.file.name}
                                style={{
                                  width: 160,
                                  height: 110,
                                  objectFit: 'cover',
                                  borderRadius: 10,
                                  display: 'block',
                                }}
                              />
                            )}

                            {item.type === 'pdf' && (
                              <a
                                href={item.url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-decoration-none"
                                style={{ color: 'inherit' }}
                              >
                                <div
                                  className="pdf-card d-flex flex-column justify-content-center align-items-center"
                                  style={{
                                    width: 160,
                                    height: 110,
                                    borderRadius: 10,
                                    border: '1px solid #ddd',
                                    padding: 10,
                                    background: '#fff',
                                    cursor: 'pointer',
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={faFilePdf}
                                    style={{ fontSize: 36, color: '#e11d48' }}
                                  />

                                  <small
                                    className="text-muted text-truncate mt-2"
                                    style={{ maxWidth: 140 }}
                                    title={item.file.name}
                                  >
                                    {item.file.name}
                                  </small>

                                  <small style={{ fontSize: 12, color: '#2563eb' }}>
                                    Click to open
                                  </small>
                                </div>
                              </a>
                            )}

                            {item.type === 'other' && (
                              <div
                                className="d-flex flex-column justify-content-center align-items-center"
                                style={{
                                  width: 160,
                                  height: 110,
                                  borderRadius: 10,
                                  border: '1px solid #ddd',
                                  padding: 10,
                                  background: '#fafafa',
                                }}
                              >
                                <strong>FILE</strong>
                                <small
                                  className="text-muted text-truncate"
                                  style={{ maxWidth: 140 }}
                                  title={item.file.name}
                                >
                                  {item.file.name}
                                </small>
                              </div>
                            )}

                            {id ?
                              (me?.role === 'admin' ?
                                <button
                                  type="button"
                                  onClick={() => removeFile(index, item?.id)}
                                  style={{
                                    position: 'absolute',
                                    top: -8,
                                    right: -8,
                                    width: 26,
                                    height: 26,
                                    borderRadius: 50,
                                    border: 'none',
                                    background: '#dc3545',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    lineHeight: '26px',
                                  }}
                                >
                                  ×
                                </button>
                                :
                                null
                              )
                              :
                              <button
                                type="button"
                                onClick={() => removeFile(index)}
                                style={{
                                  position: 'absolute',
                                  top: -8,
                                  right: -8,
                                  width: 26,
                                  height: 26,
                                  borderRadius: 50,
                                  border: 'none',
                                  background: '#dc3545',
                                  color: '#fff',
                                  cursor: 'pointer',
                                  lineHeight: '26px',
                                }}
                              >
                                ×
                              </button>
                            }
                          </li>
                        ))}

                        {id ?
                          (me?.role === 'admin' ?
                            <li className="list-inline-item">
                              <label
                                htmlFor="imageUpload"
                                className="upload-box d-flex flex-column align-items-center justify-content-center p-4"
                                style={{
                                  cursor: 'pointer',
                                  border: '2px dashed #00000040',
                                  borderRadius: 10,
                                  minWidth: 160,
                                  minHeight: 110,
                                }}
                              >
                                {loading?.image ? (
                                  <>
                                    <div className="spinner-border spinner-border-sm text-primary" role="status" />
                                    <h6 className="mt-2 mb-0">Uploading...</h6>
                                  </>
                                ) : (
                                  <>
                                    <h6 className="mt-2 mb-1">Upload file</h6>
                                    <small className="allow-content">PNG, JPG, PDF allow</small>
                                  </>
                                )}
                                <input
                                  ref={inputRef}
                                  type="file"
                                  id="imageUpload"
                                  accept="image/*,application/pdf"
                                  multiple
                                  className="d-none"
                                  onChange={handleChange}
                                />
                              </label>
                            </li>
                            :
                            null)
                          :
                          <li className="list-inline-item">
                            <label
                              htmlFor="imageUpload"
                              className="upload-box d-flex flex-column align-items-center justify-content-center p-4"
                              style={{
                                cursor: 'pointer',
                                border: '2px dashed #00000040',
                                borderRadius: 10,
                                minWidth: 160,
                                minHeight: 110,
                              }}
                            >
                              {loading?.image ? (
                                <>
                                  <div className="spinner-border spinner-border-sm text-primary" role="status" />
                                  <h6 className="mt-2 mb-0">Uploading...</h6>
                                </>
                              ) : (
                                <>
                                  <h6 className="mt-2 mb-1">Upload file</h6>
                                  <small className="allow-content">PNG, JPG, PDF allow</small>
                                </>
                              )}
                              <input
                                ref={inputRef}
                                type="file"
                                id="imageUpload"
                                accept="image/*,application/pdf"
                                multiple
                                className="d-none"
                                onChange={handleChange}
                              />
                            </label>
                          </li>
                        }
                      </ul>
                    </div>
                  </div>
                  {!id &&
                    <div className="col-12">
                      <Button type="submit" loading={loading.btn} disabled={loading.btn} className="primary mx-auto">Submit</Button>
                    </div>
                  }
                </div>
              </form>

            </div>
          </div>
        </div>

        {id &&
          <div className="col-12 col-md-6 col-lg-6 col-xl-6 col-xxl-6">
            <div className="card ">
              <div className="card-header p-3">
                <h4 className="mb-0">{`${t('Items')}`}</h4>
              </div>

              <div className="card-body">
                <form onSubmit={searchFormik.handleSubmit} className={`form-loading ${loading.search == true ? 'btn-loading' : ''}`}>
                  <SearchField
                    className="col-12"
                    name="q"
                    disabled={!searchFormik.values.q}
                    autoFocus={true}
                    type="search"
                    autoComplete="off"
                    value={searchFormik.values.q}
                    onChange={(e: _Object) => {
                      searchFormik.setFieldValue('q', e.target.value)
                      setProducts(null)
                    }}
                  />
                </form>

                {searchFormik?.values?.q && products?.length === 1 &&
                  <div className="position-relative">
                    <table className="table table-striped caption-top sales-table h-auto">
                      <tbody>
                        {products?.map((item: _Object, index: number) => {
                          return (

                            <tr key={index} className="align-middle">
                              <td className="text-start break-spaces" style={{ width: '100%' }}>
                                <h4 className="mb-3">{capitalizeFirstLetter(item.name)}</h4>
                                <div className="d-flex gap-4">
                                  <small>{t('common.sku')}:&nbsp;{item.sku}</small>
                                  <small>{t('common.stock')}:&nbsp;{stock_status(item.stock_status)} {item.manage_stock == 'true' && item.stock_status == 'instock' && <small>{item.stock}</small>}</small>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>}

                <form onSubmit={addItemFormik.handleSubmit}>
                  <div className="row">
                    <InputField
                      className="col-6"
                      type="number"
                      name="quantity"
                      label={t('common.quantity')}
                      required={true}
                      value={addItemFormik.values.quantity}
                      onChange={addItemFormik.handleChange}
                      onBlur={addItemFormik.handleBlur}
                      error={addItemFormik.touched.quantity && addItemFormik.errors.quantity}
                    />

                    <div className="col-6">
                      <AsyncSelectField
                        isClearable
                        cacheOptions
                        required={true}
                        label={`${t('location.location')}`}
                        name="location_id"
                        placeholder={t('common.choose_option')}
                        loadOptions={locationOption}
                        ref={(ref: any) => (selectAsyncSelect.current = ref)}
                        defaultOptions={locations?.map((item: _Object) => { return { label: item.name, value: item.id } })}
                        onChange={(val: _Object) => {
                          addItemFormik.setFieldValue('location_id', val?.value || '')
                        }}
                        value={
                          locations
                            ?.map((item: _Object) => ({ label: item.name, value: item.id }))
                            .find((opt: any) => opt.value === addItemFormik.values.location_id) || null
                        }
                        onBlur={addItemFormik.handleBlur}
                        // disabled={!(addItemFormik.values.product_id)}
                        error={addItemFormik.touched.location_id && addItemFormik.errors?.location_id}
                      />
                    </div>
                    <div className="col-12">
                      <Button disabled={loading.item} loading={loading.item} className="primary mx-auto" type="submit">Add item</Button>
                    </div>
                  </div>
                </form>


              </div>
            </div>


            {items.length > 0 &&
              <div className="mt-4">
                <div className="card-header pb-3">
                  <h4 className="mb-0">Recently added</h4>
                </div>
                <div className="card-body p-0">

                  <div className="table-responsive sales-table ">
                    <table className="table table-striped caption-top sales-table table-bordered order-payments z-0">
                      <thead className="sticky-top">
                        <tr className="h-auto">
                          <th style={{ width: '50px' }}>#</th>
                          <th style={{ width: '300px' }}>Name</th>
                          <th>Quantity</th>
                          <th>SKU</th>
                          <th style={{ width: '160px' }}>Action</th>

                        </tr>
                      </thead>
                      <tbody className="h-auto">

                        {
                          items.map((item: any, i: number) => {
                            return (
                              <tr className="h-auto" key={i}>
                                <td>{i + 1}</td>
                                <td>{item?.product?.name}</td>
                                <td>{item.quantity}</td>
                                <td>{item.product.sku}</td>
                                <td>
                                  <div className="justify-content-end d-flex rounded-0">
                                    <Link className="btn btn-link p-0 text-dark" to={`/app/products/edit/${item.product_id}`}><svg className="svg-inline--fa fa-eye" aria-hidden="true" focusable="false" data-prefix="fas" data-icon="eye" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" data-fa-i2svg=""><path fill="currentColor" d="M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z"></path></svg></Link>
                                  </div>
                                </td>
                              </tr>
                            )
                          })
                        }
                      </tbody>
                    </table>
                  </div>
                </div>

              </div>
            }
          </div>
        }

        {id &&
          <div className="col col-md-6 col-lg-6 col-xl-5 pt-6 mt-4">
            <p className="d-flex align-items-center"> <strong className="me-2">{t('orderDetails.payments')}</strong>
              {initialValues?.payment_status !== 'paid' &&
                <button type="button" className="btn btn-transparent p-0 border-0" data-bs-toggle="modal" data-bs-target="#addPaymentModal">
                  <FontAwesomeIcon icon={faCirclePlus} className="me-0 text-primary" />
                </button>
              }
              {/* Modal */}
              <div className="modal fade" id="addPaymentModal" tabIndex={-1} aria-labelledby="addPaymentModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                  <form className="modal-content" onSubmit={addPaymentFormik.handleSubmit}>
                    <div className="modal-header">
                      <aside>
                        <h4 className="mb-0">{t('orderDetails.add_payment')}</h4>
                      </aside>
                      <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                      <div className="row">
                        <div className="form-group col-12 col-md-6">
                          <label className="label-form mb-1">{t('common.date')} {<span className="text-danger">*</span>}</label>

                          <DatePicker
                            dateFormat="MMM dd, yyyy"
                            className={`${addPaymentFormik.touched.payment_date && addPaymentFormik.errors.payment_date && 'invalid'}`}
                            placeholderText="Enter here"
                            onChange={(value: any) => {
                              const selectedDate = moment(value).format('YYYY-MM-DD')
                              addPaymentFormik.setFieldValue('payment_date', selectedDate)
                            }}
                            selected={addPaymentFormik.values.payment_date ? moment(addPaymentFormik.values.payment_date, 'YYYY-MM-DD').toDate() : null}
                            maxDate={new Date()}
                          />

                          {addPaymentFormik.touched.payment_date && addPaymentFormik.errors.payment_date && (
                            <span className="invalid-feedback text-danger d-block mt-1">
                              {addPaymentFormik.errors.payment_date}
                            </span>
                          )}
                        </div>

                        <InputField
                          label={t('orderDetails.reference_no')}
                          name="payment_ref_number"
                          value={addPaymentFormik.values.payment_ref_number}
                          onChange={addPaymentFormik.handleChange}
                          className="col-12 col-md-6"
                          error={addPaymentFormik.touched.payment_ref_number && addPaymentFormik.errors.payment_ref_number}

                        />
                        <InputField
                          label={t('common.amount')}
                          type="number"
                          required="true"
                          name="amount"
                          value={addPaymentFormik.values.amount}
                          onBlur={addPaymentFormik.handleBlur}
                          onChange={addPaymentFormik.handleChange}
                          className="col-12 col-md-6"
                          error={addPaymentFormik.touched.amount && addPaymentFormik.errors.amount}
                        />

                        <SelectField
                          name="payment_mode"
                          label={t('checkout.payment_mode')}
                          required="true"
                          className="col-12 col-md-6"
                          value={{ value: addPaymentFormik.values.payment_mode }}
                          onBlur={addPaymentFormik.handleBlur}
                          // options={me.client?.payment_methods?.map((item: string) => ({
                          //   value: item,
                          //   label: getPaymentModeLabel(item),
                          // }))}
                          options={[
                            { label: 'Cash', value: 'cash' },
                            { label: 'Card', value: 'card' },
                            { label: 'UPI', value: 'upi' },
                          ]}
                          //   // { label: 'PhonePe', value: 'phonepe' },
                          //   // { label: 'Paytm', value: 'paytm' },
                          //   // { label: 'Google Pay', value: 'googlepay' },
                          // ]}
                          onChange={(e: _Object) => {
                            addPaymentFormik.setFieldValue('payment_mode', e?.value || '')
                          }}
                          error={addPaymentFormik.touched.payment_mode && addPaymentFormik.errors.payment_mode}
                          isClearable
                        />

                      </div>
                    </div>
                    <div className="modal-footer">
                      <Button name={t('common.cancel')} type="button" onClick={() => addPaymentFormik.resetForm()} data-bs-dismiss="modal" className="link text-danger" />
                      <Button
                        loading={loading.payment}
                        className="primary"
                        name={t('common.submit')}
                        disabled={
                          !addPaymentFormik.values.amount ||
                          !addPaymentFormik.values.payment_mode ||
                          !addPaymentFormik.values.payment_date ||
                          (addPaymentFormik.touched.amount && addPaymentFormik.errors.amount)
                        }
                      />

                    </div>
                  </form>
                </div>
              </div>
            </p>
            <table className="table table-bordered table-striped order-payments z-0">
              <thead>
                <tr className="h-auto">
                  <th style={{ width: '160px' }}>{t('common.date')}</th>
                  <th>{t('orderDetails.ref_no')} #</th>
                  <th>{t('common.amount')}</th>
                  <th style={{ width: '160px', textAlign: 'left' }}>{t('orderDetails.payment_mode')}</th>
                </tr>
              </thead>
              <tbody className="h-auto">
                {payments?.map((item: _Object, i: number) => {
                  return (
                    <tr key={i} className="h-auto">
                      <td>{doDateFormatWithTime(item.created_at)}</td>
                      <td>{item.payment_ref_number ? capitalizeFirstLetter(item.payment_ref_number) : '-'}</td>
                      <td>{doAmountFormat(item.amount)}</td>
                      <td style={{ textAlign: 'left' }}>{getPaymentModeLabel(item.payment_mode)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        }
      </div>

      {searchFormik?.values?.q && products?.length > 1 &&
        <div className={`modal fade ${modalState ? ' show d-block' : ' d-none'}`} id="exampleModal" role="dialog" aria-hidden="true">
          <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="mb-0">{t('common.search_result')}</h4>
                <button type="button" className="btn-close" onClick={() => setModalState(!modalState)} data-bs-dismiss="modal" aria-label="Close"></button>
              </div>

              <div className="table-responsive sales-table">
                <table className="table table-striped caption-top sales-table">
                  <thead className="sticky-top">
                    <tr>
                      <th>{t('common.name')}</th>
                      <th style={{ width: '160px' }}>{t('common.sku')}</th>
                      <th style={{ width: '120px' }}>{t('common.stock')}</th>
                      <th className="text-end" style={{ width: '50px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {products?.map((item: any, i: number) => {
                      return (
                        <tr key={i}>
                          <td className="break-spaces">{capitalizeFirstLetter(item.name)}</td>
                          <td style={{ width: '200px' }}>{item.sku}</td>
                          <td style={{ width: '200px' }}>{stock_status(item.stock_status)} {item.manage_stock == 'true' && item.stock_status == 'instock' && <small>{item.stock}</small>}</td>
                          <td className="text-center">
                            <input
                              className="form-check-input mt-0"
                              type="radio"
                              name="flexRadioDefault"
                              id="flexRadioDefault1"
                              onChange={() => {
                                addItemFormik.setFieldValue('product_id', item.id);
                                addItemFormik.setFieldValue('product', item);
                              }}
                            />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              <div className="modal-footer">
                <Button type="button" name={t('common.cancel')} className="link text-danger" onClick={() => setModalState(!modalState)} />
                <Button
                  type="button"
                  name={t('common.submit')}
                  className="primary"
                  disabled={!(addItemFormik.values.product_id)}
                  onClick={() => { setProducts([addItemFormik?.values?.product]); setModalState(!modalState) }}
                />
              </div>
            </div>
          </div>
        </div>}
    </>
  )
}

export default PurchaseReportCreate